import { assertFinite, FormatInputError, numberFormat, unsigned0 } from "./errors";

// Energy and power with explicit input units and precision presets that match
// today's displays (LOC-104 section 3): meter readings 3 decimals, session
// summaries 2 decimals, small totals in Wh. The input unit is part of the
// call, so a value is never assumed to be Wh or kWh.

export type EnergyUnit = "Wh" | "kWh";
export type EnergyPreset = "meter" | "summary" | "auto";
export type PowerUnit = "W" | "kW";
export type PowerPreset = "standard" | "precise";

function fixed(value: number, digits: number): string {
  return numberFormat({ minimumFractionDigits: digits, maximumFractionDigits: digits, signDisplay: "negative" }).format(unsigned0(value));
}

/**
 * meter: "12.345 kWh"; summary: "12.35 kWh";
 * auto: "12.35 kWh" from 1 kWh up, below that "850 Wh" (receipt and summary style).
 */
export function formatEnergy(value: number, unit: EnergyUnit, preset: EnergyPreset = "summary"): string {
  if (unit !== "Wh" && unit !== "kWh") throw new FormatInputError("unit", String(unit));
  assertFinite(value, "energy");
  const kwh = unit === "kWh" ? value : value / 1000;
  if (preset === "auto" && Math.abs(kwh) < 1) return `${fixed(kwh * 1000, 0)} Wh`;
  return `${fixed(kwh, preset === "meter" ? 3 : 2)} kWh`;
}

/** standard: "7.4 kW"; precise: "7.36 kW". */
export function formatPower(value: number, unit: PowerUnit, preset: PowerPreset = "standard"): string {
  if (unit !== "W" && unit !== "kW") throw new FormatInputError("unit", String(unit));
  assertFinite(value, "power");
  const kw = unit === "kW" ? value : value / 1000;
  return `${fixed(kw, preset === "precise" ? 2 : 1)} kW`;
}

/** Plain grouped number with a fixed number of decimals: formatNumber(1234.5, 1) -> "1,234.5". */
export function formatNumber(value: number, fractionDigits = 0): string {
  assertFinite(value, "number");
  return fixed(value, fractionDigits);
}

/** Ratio 0..1 as a percentage: formatPercent(0.42) -> "42%", formatPercent(0.425, 1) -> "42.5%". */
export function formatPercent(ratio: number, fractionDigits = 0): string {
  assertFinite(ratio, "ratio");
  return numberFormat({ style: "percent", minimumFractionDigits: fractionDigits, maximumFractionDigits: fractionDigits, signDisplay: "negative" }).format(unsigned0(ratio));
}
